/**
 * CRM Domain Seed Data
 *
 * Contains all seed data for the CRM Service (34):
 * - Leads (sales pipeline)
 * - Partners (CRM representation linked to consumers)
 * - Deals (loan deals in progress)
 * - Referrals (partner-submitted referrals)
 * - Commissions (partner payouts)
 * - Commission Settings (business-level config)
 * - Commission Tiers (volume-based rates)
 */

import { seedId } from '../utils';
import { CASE_IDS } from './cases';
import { CONSUMER_USER_IDS, PARTNER_USER_IDS, PARTNERS } from './consumers';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// LEAD IDs
// =============================================================================

export const LEAD_IDS = {
  // Hot Leads (score 80+)
  JAMES_WALKER: seedId('lead-james', 1),
  EMILY_CHEN: seedId('lead-emily', 2),

  // Warm Leads (score 50-79)
  CARLOS_RIVERA: seedId('lead-carlos', 3),
  SARAH_JOHNSON: seedId('lead-sarah', 4),
  ANTHONY_NGUYEN: seedId('lead-anthony', 5),

  // Cold Leads (score 0-49)
  MICHELLE_DAVIS: seedId('lead-michelle', 6),
  RYAN_PATEL: seedId('lead-ryan', 7),
  LISA_WONG: seedId('lead-lisa', 8),

  // Campaign-associated Leads (for marketing campaigns)
  LEAD_MARCUS: seedId('lead-marcus', 9),
  LEAD_ROBERT: seedId('lead-robert', 10),
  LEAD_ALICIA: seedId('lead-alicia', 11),
  LEAD_JENNIFER: seedId('lead-jennifer', 12),
  LEAD_KEVIN: seedId('lead-kevin', 13),
} as const;

export type LeadId = (typeof LEAD_IDS)[keyof typeof LEAD_IDS];

// =============================================================================
// CRM PARTNER IDs (links to Consumer Partners)
// =============================================================================

export const CRM_PARTNER_IDS = {
  PREMIER_MORTGAGE_CRM: seedId('crm-partner-premier', 1),
  AUSTIN_MORTGAGE_CRM: seedId('crm-partner-austin', 2),
  FLORIDA_FIRST_CRM: seedId('crm-partner-florida', 3),
  CAPITAL_FUNDING_CRM: seedId('crm-partner-capital', 4),
  RE_INVESTOR_ASSOC_CRM: seedId('crm-partner-reinvest', 5),
  PACIFIC_WHOLESALE_CRM: seedId('crm-partner-pacific', 6),
  // Consumer referral partner (Marcus Thompson — VIP consumer who also refers clients)
  MARCUS_THOMPSON_REFERRAL: seedId('crm-partner-marcus', 7),
  // E2E Test Partners (Issue #285, #470 - Required for E2E canary scenarios)
  // Issue #470: Use seedId() to generate valid UUIDs for canary validation
  E2E_TEST_PARTNER: seedId('e2e-partner', 1),
} as const;

/**
 * E2E Test Owner IDs (Issue #285, #470)
 * These are user IDs used in E2E canary scenarios for deal ownership tests.
 * They don't need to be actual users - just consistent IDs for testing.
 * Issue #470: Use seedId() to generate valid UUIDs for canary validation.
 */
export const E2E_TEST_OWNER_IDS = {
  OWNER_1: seedId('e2e-owner', 1),
  OWNER_2: seedId('e2e-owner', 2),
} as const;

export type CrmPartnerId = (typeof CRM_PARTNER_IDS)[keyof typeof CRM_PARTNER_IDS];

// =============================================================================
// REFERRAL IDs
// =============================================================================

export const REFERRAL_IDS = {
  // Pending referrals
  REF_MARTINEZ_BUYER: seedId('ref-martinez', 1),
  REF_JOHNSON_INVESTOR: seedId('ref-johnson', 2),
  REF_GARCIA_SELLER: seedId('ref-garcia', 3),

  // Converted referrals
  REF_CHEN_CONVERTED: seedId('ref-chen', 4),
  REF_WILLIAMS_QUALIFIED: seedId('ref-williams', 5),

  // === EXPANDED REFERRALS (40+ total) ===
  // Pending Status (new submissions)
  REF_ANDERSON_BUYER: seedId('ref-anderson', 6),
  REF_TAYLOR_INVESTOR: seedId('ref-taylor', 7),
  REF_THOMAS_FLIPPER: seedId('ref-thomas', 8),
  REF_JACKSON_BUYER: seedId('ref-jackson', 9),
  REF_WHITE_LANDLORD: seedId('ref-white', 10),
  REF_HARRIS_DEVELOPER: seedId('ref-harris', 11),
  REF_MARTIN_INVESTOR: seedId('ref-martin', 12),
  REF_THOMPSON_REFI: seedId('ref-thompson-r', 13),

  // Contacted Status (initial outreach done)
  REF_CLARK_BUYER: seedId('ref-clark', 14),
  REF_ROBINSON_INVESTOR: seedId('ref-robinson', 15),
  REF_LEWIS_FLIPPER: seedId('ref-lewis', 16),
  REF_LEE_BUYER: seedId('ref-lee', 17),
  REF_WALKER_LANDLORD: seedId('ref-walker', 18),
  REF_HALL_DEVELOPER: seedId('ref-hall', 19),
  REF_ALLEN_INVESTOR: seedId('ref-allen', 20),
  REF_YOUNG_REFI: seedId('ref-young', 21),

  // Qualified Status (verified prospects)
  REF_HERNANDEZ_BUYER: seedId('ref-hernandez', 22),
  REF_KING_INVESTOR: seedId('ref-king', 23),
  REF_WRIGHT_FLIPPER: seedId('ref-wright', 24),
  REF_LOPEZ_BUYER: seedId('ref-lopez', 25),
  REF_HILL_LANDLORD: seedId('ref-hill', 26),
  REF_SCOTT_DEVELOPER: seedId('ref-scott', 27),
  REF_GREEN_INVESTOR: seedId('ref-green', 28),
  REF_ADAMS_REFI: seedId('ref-adams', 29),

  // Converted Status (became deals)
  REF_BAKER_CONVERTED: seedId('ref-baker-conv', 30),
  REF_GONZALEZ_CONVERTED: seedId('ref-gonzalez-conv', 31),
  REF_NELSON_CONVERTED: seedId('ref-nelson-conv', 32),
  REF_CARTER_CONVERTED: seedId('ref-carter-conv', 33),
  REF_MITCHELL_CONVERTED: seedId('ref-mitchell-conv', 34),
  REF_PEREZ_CONVERTED: seedId('ref-perez-conv', 35),
  REF_ROBERTS_CONVERTED: seedId('ref-roberts-conv', 36),
  REF_TURNER_CONVERTED: seedId('ref-turner-conv', 37),

  // Lost Status (did not convert)
  REF_PHILLIPS_LOST: seedId('ref-phillips-lost', 38),
  REF_CAMPBELL_LOST: seedId('ref-campbell-lost', 39),
  REF_PARKER_LOST: seedId('ref-parker-lost', 40),
  REF_EVANS_LOST: seedId('ref-evans-lost', 41),
  REF_EDWARDS_LOST: seedId('ref-edwards-lost', 42),
  REF_COLLINS_LOST: seedId('ref-collins-lost', 43),
} as const;

export type ReferralId = (typeof REFERRAL_IDS)[keyof typeof REFERRAL_IDS];

// =============================================================================
// COMMISSION IDs
// =============================================================================

export const COMMISSION_IDS = {
  COMM_THOMPSON_DEAL: seedId('comm-thompson', 1),
  COMM_FERNANDEZ_DEAL: seedId('comm-fernandez', 2),
  COMM_WILLIAMS_REF: seedId('comm-williams', 3),
  COMM_PENDING_RIVERA: seedId('comm-rivera-pend', 4),

  // === EXPANDED COMMISSIONS (50+ total) ===
  // Paid Commissions (completed payouts)
  COMM_COLLINS_PAID: seedId('comm-collins-paid', 5),
  COMM_STEWART_PAID: seedId('comm-stewart-paid', 6),
  COMM_SANCHEZ_PAID: seedId('comm-sanchez-paid', 7),
  COMM_MORRIS_PAID: seedId('comm-morris-paid', 8),
  COMM_ROGERS_PAID: seedId('comm-rogers-paid', 9),
  COMM_REED_PAID: seedId('comm-reed-paid', 10),
  COMM_COOK_PAID: seedId('comm-cook-paid', 11),
  COMM_MORGAN_PAID: seedId('comm-morgan-paid', 12),
  COMM_BELL_PAID: seedId('comm-bell-paid', 13),
  COMM_MURPHY_PAID: seedId('comm-murphy-paid', 14),
  COMM_BAILEY_PAID: seedId('comm-bailey-paid', 15),
  COMM_RIVERA_CLOSED_PAID: seedId('comm-rivera-cl-paid', 16),
  COMM_COOPER_PAID: seedId('comm-cooper-paid', 17),
  COMM_RICHARDSON_PAID: seedId('comm-richardson-paid', 18),
  COMM_COX_PAID: seedId('comm-cox-paid', 19),
  COMM_HOWARD_PAID: seedId('comm-howard-paid', 20),

  // Approved Commissions (pending payout)
  COMM_PEREZ_APPROVED: seedId('comm-perez-appr', 21),
  COMM_ROBERTS_APPROVED: seedId('comm-roberts-appr', 22),
  COMM_TURNER_APPROVED: seedId('comm-turner-appr', 23),
  COMM_PHILLIPS_APPROVED: seedId('comm-phillips-appr', 24),
  COMM_CAMPBELL_APPROVED: seedId('comm-campbell-appr', 25),
  COMM_PARKER_APPROVED: seedId('comm-parker-appr', 26),
  COMM_EVANS_APPROVED: seedId('comm-evans-appr', 27),
  COMM_EDWARDS_APPROVED: seedId('comm-edwards-appr', 28),

  // Pending Commissions (awaiting funding)
  COMM_WALKER_PENDING: seedId('comm-walker-pend', 29),
  COMM_CHEN_PENDING: seedId('comm-chen-pend', 30),
  COMM_JOHNSON_PENDING: seedId('comm-johnson-pend', 31),
  COMM_NGUYEN_PENDING: seedId('comm-nguyen-pend', 32),
  COMM_TAYLOR_PENDING: seedId('comm-taylor-pend', 33),
  COMM_HARRIS_PENDING: seedId('comm-harris-pend', 34),
  COMM_CLARK_PENDING: seedId('comm-clark-pend', 35),
  COMM_LEWIS_PENDING: seedId('comm-lewis-pend', 36),
  COMM_ROBINSON_PENDING: seedId('comm-robinson-pend', 37),
  COMM_HALL_PENDING: seedId('comm-hall-pend', 38),
  COMM_YOUNG_PENDING: seedId('comm-young-pend', 39),
  COMM_KING_PENDING: seedId('comm-king-pend', 40),

  // Referral Commissions (flat fees)
  COMM_REF_BAKER: seedId('comm-ref-baker', 41),
  COMM_REF_GONZALEZ: seedId('comm-ref-gonzalez', 42),
  COMM_REF_NELSON: seedId('comm-ref-nelson', 43),
  COMM_REF_CARTER: seedId('comm-ref-carter', 44),
  COMM_REF_MITCHELL: seedId('comm-ref-mitchell', 45),
  COMM_REF_PEREZ: seedId('comm-ref-perez', 46),
  COMM_REF_ROBERTS: seedId('comm-ref-roberts', 47),
  COMM_REF_TURNER: seedId('comm-ref-turner', 48),

  // Cancelled Commissions (deal fell through)
  COMM_CANCELLED_PHILLIPS: seedId('comm-canc-phillips', 49),
  COMM_CANCELLED_CAMPBELL: seedId('comm-canc-campbell', 50),
  COMM_CANCELLED_PARKER: seedId('comm-canc-parker', 51),
  COMM_CANCELLED_EVANS: seedId('comm-canc-evans', 52),
  COMM_CANCELLED_EDWARDS: seedId('comm-canc-edwards', 53),
} as const;

export type CommissionId = (typeof COMMISSION_IDS)[keyof typeof COMMISSION_IDS];

// =============================================================================
// COMMISSION TIER IDs
// =============================================================================

export const COMMISSION_TIER_IDS = {
  // OpsMatrix Capital tiers
  OPSMATRIX_BRONZE: seedId('tier-ops-bronze', 1),
  OPSMATRIX_SILVER: seedId('tier-ops-silver', 2),
  OPSMATRIX_GOLD: seedId('tier-ops-gold', 3),
  OPSMATRIX_PLATINUM: seedId('tier-ops-platinum', 4),

  // 1Sharpe Capital tiers
  SHARPE_STANDARD: seedId('tier-sharpe-std', 5),
  SHARPE_PREFERRED: seedId('tier-sharpe-pref', 6),
  SHARPE_ELITE: seedId('tier-sharpe-elite', 7),
} as const;

export type CommissionTierId = (typeof COMMISSION_TIER_IDS)[keyof typeof COMMISSION_TIER_IDS];

// =============================================================================
// COMMISSION SETTINGS IDs
// =============================================================================

export const COMMISSION_SETTINGS_IDS = {
  OPSMATRIX_SETTINGS: seedId('comm-settings-ops', 1),
  SHARPE_SETTINGS: seedId('comm-settings-sharpe', 2),
  PACIFIC_SETTINGS: seedId('comm-settings-pacific', 3),
  GULFCOAST_SETTINGS: seedId('comm-settings-gulf', 4),
} as const;

export type CommissionSettingsId = (typeof COMMISSION_SETTINGS_IDS)[keyof typeof COMMISSION_SETTINGS_IDS];

// =============================================================================
// LEAD STATUS & QUALIFICATION
// =============================================================================

export const LEAD_STATUS = {
  NEW: 'new',
  CONTACTED: 'contacted',
  QUALIFIED: 'qualified',
  CONVERTED: 'converted',
  LOST: 'lost',
} as const;

export const LEAD_QUALIFICATION = {
  UNQUALIFIED: 'UNQUALIFIED',
  MQL: 'MQL', // Marketing Qualified Lead
  SQL: 'SQL', // Sales Qualified Lead
} as const;

// =============================================================================
// REFERRAL STATUS
// =============================================================================

export const REFERRAL_STATUS = {
  PENDING: 'pending',
  CONTACTED: 'contacted',
  QUALIFIED: 'qualified',
  CONVERTED: 'converted',
  LOST: 'lost',
} as const;

export const REFERRAL_TYPE = {
  BUYER: 'buyer',
  SELLER: 'seller',
  INVESTOR: 'investor',
} as const;

// =============================================================================
// COMMISSION STATUS
// =============================================================================

export const COMMISSION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  PAID: 'paid',
  CANCELLED: 'cancelled',
} as const;

// =============================================================================
// PAYOUT SCHEDULE
// =============================================================================

export const PAYOUT_SCHEDULE = {
  WEEKLY: 'WEEKLY',
  BIWEEKLY: 'BIWEEKLY',
  MONTHLY: 'MONTHLY',
} as const;

// =============================================================================
// LEAD DATA
// =============================================================================

export interface LeadSeedData {
  id: string;
  businessId: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  score: number;
  qualification: string;
  status: string;
  source?: string;
  notes?: string;
  // Property Info
  propertyAddress?: string;
  propertyType?: string;
  purchasePrice?: number;
  // Loan Fields
  loanAmount?: number;
  borrowerName?: string;
  borrowerEntity?: string;
  contactPhone?: string;
  arv?: number; // After Repair Value
  rehabBudget?: number;
  ltv?: number; // Loan to Value ratio
  ltc?: number; // Loan to Cost ratio
  interestRate?: number;
  loanTerm?: number; // months
  originationPoints?: number;
  extendedData?: Record<string, unknown>;
  createdBy: string;
}

export const LEADS: Record<keyof typeof LEAD_IDS, LeadSeedData> = {
  // Hot Leads
  JAMES_WALKER: {
    id: LEAD_IDS.JAMES_WALKER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'James Walker',
    email: 'james.walker@walkerinvest.com',
    phone: '+1-512-555-3001',
    company: 'Walker Investment Group',
    score: 92,
    qualification: LEAD_QUALIFICATION.SQL,
    status: LEAD_STATUS.QUALIFIED,
    source: 'Website Inquiry',
    notes: 'Experienced fix-and-flip investor. Looking for bridge loan.',
    propertyAddress: '1234 Oak Street, Austin, TX 78701',
    propertyType: 'Single Family',
    purchasePrice: 425000,
    loanAmount: 340000,
    borrowerName: 'James Walker',
    borrowerEntity: 'Walker Investment Group LLC',
    arv: 575000,
    rehabBudget: 85000,
    ltv: 0.8,
    ltc: 0.67,
    interestRate: 0.0725,
    loanTerm: 12,
    originationPoints: 0.02,
    createdBy: 'system',
  },
  EMILY_CHEN: {
    id: LEAD_IDS.EMILY_CHEN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Emily Chen',
    email: 'emily@chenrealtygroup.com',
    phone: '+1-713-555-3002',
    company: 'Chen Realty Group',
    score: 85,
    qualification: LEAD_QUALIFICATION.SQL,
    status: LEAD_STATUS.CONTACTED,
    source: 'Partner Referral',
    notes: 'Looking to finance multi-family acquisition.',
    propertyAddress: '5678 Commerce Blvd, Houston, TX 77002',
    propertyType: 'Multi-Family (8 units)',
    purchasePrice: 1250000,
    loanAmount: 937500,
    borrowerName: 'Emily Chen',
    borrowerEntity: 'Chen Realty Holdings LLC',
    ltv: 0.75,
    interestRate: 0.08,
    loanTerm: 24,
    originationPoints: 0.015,
    createdBy: 'system',
  },

  // Warm Leads
  CARLOS_RIVERA: {
    id: LEAD_IDS.CARLOS_RIVERA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Carlos Rivera',
    email: 'carlos@riverahomes.net',
    phone: '+1-210-555-3003',
    company: 'Rivera Homes LLC',
    score: 68,
    qualification: LEAD_QUALIFICATION.MQL,
    status: LEAD_STATUS.CONTACTED,
    source: 'Trade Show',
    notes: 'Interested in construction financing. Has 3 projects lined up.',
    propertyAddress: '2020 Builder Way, San Antonio, TX 78204',
    propertyType: 'New Construction',
    purchasePrice: 180000, // Lot price
    loanAmount: 650000, // Total construction loan
    borrowerName: 'Carlos Rivera',
    borrowerEntity: 'Rivera Homes LLC',
    arv: 850000,
    rehabBudget: 470000,
    ltc: 0.76,
    loanTerm: 18,
    createdBy: 'system',
  },
  SARAH_JOHNSON: {
    id: LEAD_IDS.SARAH_JOHNSON,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Sarah Johnson',
    email: 'sarah.johnson@jredev.com',
    phone: '+1-305-555-3004',
    company: 'JRE Development',
    score: 72,
    qualification: LEAD_QUALIFICATION.MQL,
    status: LEAD_STATUS.QUALIFIED,
    source: 'LinkedIn',
    notes: 'Commercial developer. Needs bridge financing for mixed-use project.',
    propertyAddress: '100 Downtown Plaza, Miami, FL 33101',
    propertyType: 'Mixed-Use Commercial',
    purchasePrice: 3200000,
    loanAmount: 2400000,
    ltv: 0.75,
    interestRate: 0.085,
    loanTerm: 36,
    createdBy: 'system',
  },
  ANTHONY_NGUYEN: {
    id: LEAD_IDS.ANTHONY_NGUYEN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Anthony Nguyen',
    email: 'anthony@nguyenproperties.com',
    phone: '+1-469-555-3005',
    company: 'Nguyen Properties',
    score: 55,
    qualification: LEAD_QUALIFICATION.MQL,
    status: LEAD_STATUS.NEW,
    source: 'Cold Outreach',
    notes: 'First-time investor. Has cash for down payment, needs financing.',
    propertyAddress: '789 Investor Lane, Dallas, TX 75201',
    propertyType: 'Single Family',
    purchasePrice: 285000,
    loanAmount: 228000,
    ltv: 0.8,
    loanTerm: 12,
    createdBy: 'system',
  },

  // Cold Leads
  MICHELLE_DAVIS: {
    id: LEAD_IDS.MICHELLE_DAVIS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Michelle Davis',
    email: 'michelle.davis@gmail.com',
    phone: '+1-512-555-3006',
    score: 35,
    qualification: LEAD_QUALIFICATION.UNQUALIFIED,
    status: LEAD_STATUS.NEW,
    source: 'Website Form',
    notes: 'General inquiry. Not sure about investment timeline.',
    createdBy: 'system',
  },
  RYAN_PATEL: {
    id: LEAD_IDS.RYAN_PATEL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Ryan Patel',
    email: 'ryan.patel@outlook.com',
    phone: '+1-813-555-3007',
    score: 28,
    qualification: LEAD_QUALIFICATION.UNQUALIFIED,
    status: LEAD_STATUS.CONTACTED,
    source: 'Referral',
    notes: 'Just exploring options. May revisit in 6 months.',
    createdBy: 'system',
  },
  LISA_WONG: {
    id: LEAD_IDS.LISA_WONG,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Lisa Wong',
    email: 'lisa.wong@wongventures.com',
    phone: '+1-415-555-3008',
    company: 'Wong Ventures',
    score: 42,
    qualification: LEAD_QUALIFICATION.UNQUALIFIED,
    status: LEAD_STATUS.NEW,
    source: 'Google Ads',
    notes: 'Interested in California market. Needs more information.',
    propertyAddress: 'TBD - San Francisco Bay Area',
    propertyType: 'Multi-Family',
    createdBy: 'system',
  },

  // Campaign-associated Leads (for marketing campaigns)
  LEAD_MARCUS: {
    id: LEAD_IDS.LEAD_MARCUS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Marcus Thompson',
    email: 'marcus.thompson@thompsoninvest.com',
    phone: '+1-512-555-4001',
    company: 'Thompson Investment Partners',
    score: 78,
    qualification: LEAD_QUALIFICATION.MQL,
    status: LEAD_STATUS.NEW,
    source: 'Email Campaign',
    notes: 'Responded to Q1 Bridge Loan email campaign.',
    propertyAddress: '456 Investment Ave, Austin, TX 78702',
    propertyType: 'Single Family',
    purchasePrice: 380000,
    loanAmount: 304000,
    ltv: 0.8,
    createdBy: 'system',
  },
  LEAD_ROBERT: {
    id: LEAD_IDS.LEAD_ROBERT,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Robert Chen',
    email: 'robert.chen@chenholdings.com',
    phone: '+1-713-555-4002',
    company: 'Chen Holdings LLC',
    score: 65,
    qualification: LEAD_QUALIFICATION.MQL,
    status: LEAD_STATUS.CONTACTED,
    source: 'Email Campaign',
    notes: 'Clicked through construction loan campaign. Scheduled call.',
    propertyAddress: '789 Builder St, Houston, TX 77003',
    propertyType: 'New Construction',
    purchasePrice: 225000,
    loanAmount: 750000,
    ltc: 0.78,
    createdBy: 'system',
  },
  LEAD_ALICIA: {
    id: LEAD_IDS.LEAD_ALICIA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Alicia Martinez',
    email: 'alicia@martinezrealty.com',
    phone: '+1-210-555-4003',
    company: 'Martinez Realty Group',
    score: 52,
    qualification: LEAD_QUALIFICATION.MQL,
    status: LEAD_STATUS.NEW,
    source: 'Email Campaign',
    notes: 'Opened investor webinar invitation. First-time investor.',
    createdBy: 'system',
  },
  LEAD_JENNIFER: {
    id: LEAD_IDS.LEAD_JENNIFER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Jennifer Williams',
    email: 'jennifer@williamsdev.com',
    phone: '+1-469-555-4004',
    company: 'Williams Development',
    score: 71,
    qualification: LEAD_QUALIFICATION.MQL,
    status: LEAD_STATUS.QUALIFIED,
    source: 'Email Campaign',
    notes: 'Responded to multi-family campaign. Has 2 properties in pipeline.',
    propertyType: 'Multi-Family (4 units)',
    purchasePrice: 650000,
    loanAmount: 520000,
    ltv: 0.8,
    createdBy: 'system',
  },
  LEAD_KEVIN: {
    id: LEAD_IDS.LEAD_KEVIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Kevin Park',
    email: 'kevin.park@parkventures.net',
    phone: '+1-214-555-4005',
    company: 'Park Ventures',
    score: 45,
    qualification: LEAD_QUALIFICATION.UNQUALIFIED,
    status: LEAD_STATUS.NEW,
    source: 'Email Campaign',
    notes: 'Unsubscribed after first email. May follow up in Q3.',
    createdBy: 'system',
  },
};

// =============================================================================
// CRM PARTNER DATA
// =============================================================================

export interface CrmPartnerSeedData {
  id: string;
  businessId: string;
  consumerId: string; // Links to Consumer in Service 29
  type: 'white_label' | 'referral';
  status: 'pending_verification' | 'active' | 'inactive' | 'suspended';
  commissionRate: number; // Decimal(5, 2) e.g., 2.50 for 2.5%
  metadata?: Record<string, unknown>;
  verifiedAt?: Date;
  verifiedBy?: string;
}

export const CRM_PARTNERS: Record<keyof typeof CRM_PARTNER_IDS, CrmPartnerSeedData> = {
  PREMIER_MORTGAGE_CRM: {
    id: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: PARTNER_USER_IDS.PREMIER_MORTGAGE, // Links via user ID for consumer-portal auth lookup
    type: 'referral',
    status: 'active',
    commissionRate: 1.5,
    metadata: {
      monthlyVolume: PARTNERS.PREMIER_MORTGAGE.monthlyVolume,
      conversionRate: PARTNERS.PREMIER_MORTGAGE.conversionRate,
      tier: 'gold',
    },
    verifiedAt: new Date('2025-06-15'),
    verifiedBy: 'system',
  },
  AUSTIN_MORTGAGE_CRM: {
    id: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: PARTNER_USER_IDS.AUSTIN_MORTGAGE, // Links via user ID for consumer-portal auth lookup
    type: 'white_label',
    status: 'active',
    commissionRate: 1.25,
    metadata: {
      monthlyVolume: PARTNERS.AUSTIN_MORTGAGE.monthlyVolume,
      conversionRate: PARTNERS.AUSTIN_MORTGAGE.conversionRate,
      tier: 'silver',
    },
    verifiedAt: new Date('2025-07-20'),
    verifiedBy: 'system',
  },
  FLORIDA_FIRST_CRM: {
    id: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: PARTNER_USER_IDS.FLORIDA_FIRST, // Links via user ID for consumer-portal auth lookup
    type: 'white_label',
    status: 'active',
    commissionRate: 1.35,
    metadata: {
      monthlyVolume: PARTNERS.FLORIDA_FIRST.monthlyVolume,
      conversionRate: PARTNERS.FLORIDA_FIRST.conversionRate,
      tier: 'preferred',
    },
    verifiedAt: new Date('2025-08-10'),
    verifiedBy: 'system',
  },
  CAPITAL_FUNDING_CRM: {
    id: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: PARTNER_USER_IDS.CAPITAL_FUNDING, // Links via user ID for consumer-portal auth lookup
    type: 'referral',
    status: 'active',
    commissionRate: 0.5,
    metadata: {
      totalReferrals: PARTNERS.CAPITAL_FUNDING.totalReferrals,
      conversionRate: PARTNERS.CAPITAL_FUNDING.conversionRate,
    },
    verifiedAt: new Date('2025-05-01'),
    verifiedBy: 'system',
  },
  RE_INVESTOR_ASSOC_CRM: {
    id: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: PARTNER_USER_IDS.RE_INVESTOR_ASSOC, // Links via user ID for consumer-portal auth lookup
    type: 'referral',
    status: 'active',
    commissionRate: 0.25,
    metadata: {
      totalReferrals: PARTNERS.RE_INVESTOR_ASSOC.totalReferrals,
      conversionRate: PARTNERS.RE_INVESTOR_ASSOC.conversionRate,
      membershipType: 'association',
    },
    verifiedAt: new Date('2025-04-15'),
    verifiedBy: 'system',
  },
  PACIFIC_WHOLESALE_CRM: {
    id: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    consumerId: PARTNER_USER_IDS.PACIFIC_WHOLESALE, // Links via user ID for consumer-portal auth lookup
    type: 'white_label',
    status: 'active',
    commissionRate: 0.75,
    metadata: {
      monthlyVolume: PARTNERS.PACIFIC_WHOLESALE.monthlyVolume,
      conversionRate: PARTNERS.PACIFIC_WHOLESALE.conversionRate,
      region: 'west_coast',
    },
    verifiedAt: new Date('2025-09-01'),
    verifiedBy: 'system',
  },
  // Consumer referral partner: Marcus Thompson (VIP consumer who also refers clients)
  // Used by partner-referral-to-commission and partner-referral-to-application tests
  MARCUS_THOMPSON_REFERRAL: {
    id: CRM_PARTNER_IDS.MARCUS_THOMPSON_REFERRAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_USER_IDS.MARCUS_THOMPSON, // Must match ctx.user.id in API gateway consumer routes
    type: 'referral',
    status: 'active',
    commissionRate: 0.75,
    metadata: {
      totalReferrals: 3,
      conversionRate: 0.67,
      consumerTier: 'VIP',
    },
    verifiedAt: new Date('2025-07-01'),
    verifiedBy: 'system',
  },
  // E2E Test Partner (Issue #285, #470 - Required for E2E canary scenarios)
  E2E_TEST_PARTNER: {
    id: CRM_PARTNER_IDS.E2E_TEST_PARTNER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL, // Default to OpsMatrix Capital
    // Issue #470: Use seedId() to generate valid UUID for canary validation
    consumerId: seedId('e2e-consumer-partner', 1),
    type: 'white_label',
    status: 'active',
    commissionRate: 2.0,
    metadata: {
      isE2ETest: true,
      description: 'Partner created for E2E canary scenario testing',
    },
    verifiedAt: new Date('2025-01-01'),
    verifiedBy: 'e2e-system',
  },
};

// =============================================================================
// REFERRAL DATA
// =============================================================================

export interface ReferralSeedData {
  id: string;
  businessId: string;
  partnerId: string; // CRM Partner ID
  clientName: string;
  clientEmail: string;
  referralType: string;
  status: string;
  convertedToCase: boolean;
  notes?: string;
}

export const REFERRALS: Record<keyof typeof REFERRAL_IDS, ReferralSeedData> = {
  // Pending referrals
  REF_MARTINEZ_BUYER: {
    id: REFERRAL_IDS.REF_MARTINEZ_BUYER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'Jennifer Martinez',
    clientEmail: 'jennifer.martinez@gmail.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'First-time buyer looking for investment property financing.',
  },
  REF_JOHNSON_INVESTOR: {
    id: REFERRAL_IDS.REF_JOHNSON_INVESTOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    clientName: 'Kevin Johnson',
    clientEmail: 'kevin.johnson@outlook.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Association member. Interested in fix-and-flip financing.',
  },
  REF_GARCIA_SELLER: {
    id: REFERRAL_IDS.REF_GARCIA_SELLER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Stephanie Garcia',
    clientEmail: 'stephanie.garcia@yahoo.com',
    referralType: REFERRAL_TYPE.SELLER,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Selling investment property. May need bridge for next purchase.',
  },

  // Converted referrals
  REF_CHEN_CONVERTED: {
    id: REFERRAL_IDS.REF_CHEN_CONVERTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'Emily Chen',
    clientEmail: 'emily@chenrealtygroup.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to Deal #DEAL_CHEN_PURCHASE.',
  },
  REF_WILLIAMS_QUALIFIED: {
    id: REFERRAL_IDS.REF_WILLIAMS_QUALIFIED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    clientName: 'David Williams',
    clientEmail: 'david.williams@williamsrealty.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Qualified but waiting for property to come available.',
  },

  // === EXPANDED REFERRALS ===
  // Pending Status
  REF_ANDERSON_BUYER: {
    id: REFERRAL_IDS.REF_ANDERSON_BUYER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'James Anderson',
    clientEmail: 'james.anderson@email.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Looking for fix-and-flip financing in Austin area.',
  },
  REF_TAYLOR_INVESTOR: {
    id: REFERRAL_IDS.REF_TAYLOR_INVESTOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    clientName: 'Robert Taylor',
    clientEmail: 'robert.taylor@taylorholdings.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Association member, interested in bridge loans for rental portfolio.',
  },
  REF_THOMAS_FLIPPER: {
    id: REFERRAL_IDS.REF_THOMAS_FLIPPER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    clientName: 'Michelle Thomas',
    clientEmail: 'michelle.thomas@gmail.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'New to flipping, has cash reserves but needs financing education.',
  },
  REF_JACKSON_BUYER: {
    id: REFERRAL_IDS.REF_JACKSON_BUYER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'William Jackson',
    clientEmail: 'w.jackson@jacksonre.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Looking for multi-family acquisition financing in Tampa.',
  },
  REF_WHITE_LANDLORD: {
    id: REFERRAL_IDS.REF_WHITE_LANDLORD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    clientName: 'Patricia White',
    clientEmail: 'patricia@whiteproperties.net',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Owns 5 rentals, wants to expand portfolio with DSCR loans.',
  },
  REF_HARRIS_DEVELOPER: {
    id: REFERRAL_IDS.REF_HARRIS_DEVELOPER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Christopher Harris',
    clientEmail: 'chris@harrisdevelopment.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Commercial developer looking for construction financing.',
  },
  REF_MARTIN_INVESTOR: {
    id: REFERRAL_IDS.REF_MARTIN_INVESTOR,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    clientName: 'Barbara Martin',
    clientEmail: 'barbara.martin@martinventures.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Bay Area investor, interested in value-add multifamily.',
  },
  REF_THOMPSON_REFI: {
    id: REFERRAL_IDS.REF_THOMPSON_REFI,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'Richard Thompson',
    clientEmail: 'r.thompson@outlook.com',
    referralType: REFERRAL_TYPE.SELLER,
    status: REFERRAL_STATUS.PENDING,
    convertedToCase: false,
    notes: 'Wants to refinance existing hard money loan at lower rate.',
  },

  // Contacted Status
  REF_CLARK_BUYER: {
    id: REFERRAL_IDS.REF_CLARK_BUYER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    clientName: 'Thomas Clark',
    clientEmail: 'tclark@clarkportfolio.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Initial call completed. Has 5-property portfolio to refinance.',
  },
  REF_ROBINSON_INVESTOR: {
    id: REFERRAL_IDS.REF_ROBINSON_INVESTOR,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    clientName: 'William Robinson',
    clientEmail: 'w.robinson@robinsonindustrial.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Industrial real estate investor. Initial meeting scheduled.',
  },
  REF_LEWIS_FLIPPER: {
    id: REFERRAL_IDS.REF_LEWIS_FLIPPER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Daniel Lewis',
    clientEmail: 'dan@lewisflips.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Experienced flipper, needs higher volume lending relationship.',
  },
  REF_LEE_BUYER: {
    id: REFERRAL_IDS.REF_LEE_BUYER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    clientName: 'Jessica Lee',
    clientEmail: 'jessica@leeinvestments.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Association member. Sent application, awaiting return.',
  },
  REF_WALKER_LANDLORD: {
    id: REFERRAL_IDS.REF_WALKER_LANDLORD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    clientName: 'James Walker',
    clientEmail: 'james.walker@walkerinvest.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Repeat client referral for additional bridge financing.',
  },
  REF_HALL_DEVELOPER: {
    id: REFERRAL_IDS.REF_HALL_DEVELOPER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Barbara Hall',
    clientEmail: 'barbara@halldevgroup.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Mixed-use developer. Site visit completed.',
  },
  REF_ALLEN_INVESTOR: {
    id: REFERRAL_IDS.REF_ALLEN_INVESTOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'Timothy Allen',
    clientEmail: 'tim.allen@allenre.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'New investor with strong W2 income. Needs education on products.',
  },
  REF_YOUNG_REFI: {
    id: REFERRAL_IDS.REF_YOUNG_REFI,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    clientName: 'Patricia Young',
    clientEmail: 'pyoung@youngrentals.com',
    referralType: REFERRAL_TYPE.SELLER,
    status: REFERRAL_STATUS.CONTACTED,
    convertedToCase: false,
    notes: 'Existing client refinance. Rate lock discussion next week.',
  },

  // Qualified Status
  REF_HERNANDEZ_BUYER: {
    id: REFERRAL_IDS.REF_HERNANDEZ_BUYER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    clientName: 'Maria Hernandez',
    clientEmail: 'maria@hernandezholdings.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Pre-approved. Searching for right property in San Antonio market.',
  },
  REF_KING_INVESTOR: {
    id: REFERRAL_IDS.REF_KING_INVESTOR,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    clientName: 'Linda King',
    clientEmail: 'linda@kingproperties.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Qualified for $2M credit line. Awaiting property selection.',
  },
  REF_WRIGHT_FLIPPER: {
    id: REFERRAL_IDS.REF_WRIGHT_FLIPPER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    clientName: 'George Wright',
    clientEmail: 'george@wrightconstruction.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Construction loan qualified. Permits pending on first project.',
  },
  REF_LOPEZ_BUYER: {
    id: REFERRAL_IDS.REF_LOPEZ_BUYER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Maria Lopez',
    clientEmail: 'mlopez@lopezrealty.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Qualified for DSCR loan. Under contract on 4-plex.',
  },
  REF_HILL_LANDLORD: {
    id: REFERRAL_IDS.REF_HILL_LANDLORD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    clientName: 'Steven Hill',
    clientEmail: 'steve.hill@hillrentals.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Portfolio refinance qualified. Gathering rent rolls.',
  },
  REF_SCOTT_DEVELOPER: {
    id: REFERRAL_IDS.REF_SCOTT_DEVELOPER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Nancy Scott',
    clientEmail: 'nancy@scottcommercial.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Commercial loan qualified. Waiting on tenant lease signatures.',
  },
  REF_GREEN_INVESTOR: {
    id: REFERRAL_IDS.REF_GREEN_INVESTOR,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'Kenneth Green',
    clientEmail: 'ken@greenrentals.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Pre-qualified for $500K. Evaluating 2 triplex options.',
  },
  REF_ADAMS_REFI: {
    id: REFERRAL_IDS.REF_ADAMS_REFI,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    clientName: 'Helen Adams',
    clientEmail: 'helen.adams@adamsgroup.com',
    referralType: REFERRAL_TYPE.SELLER,
    status: REFERRAL_STATUS.QUALIFIED,
    convertedToCase: false,
    notes: 'Cash-out refi qualified. Appraisal scheduled next week.',
  },

  // Converted Status
  REF_BAKER_CONVERTED: {
    id: REFERRAL_IDS.REF_BAKER_CONVERTED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Edward Baker',
    clientEmail: 'ed@bakerdevelopment.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to construction loan deal. Funding next month.',
  },
  REF_GONZALEZ_CONVERTED: {
    id: REFERRAL_IDS.REF_GONZALEZ_CONVERTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'Maria Gonzalez',
    clientEmail: 'maria.gonzalez@gonzalezre.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to bridge loan. Closed last week.',
  },
  REF_NELSON_CONVERTED: {
    id: REFERRAL_IDS.REF_NELSON_CONVERTED,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    clientName: 'Dorothy Nelson',
    clientEmail: 'dorothy@nelsonstorage.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to self-storage commercial loan.',
  },
  REF_CARTER_CONVERTED: {
    id: REFERRAL_IDS.REF_CARTER_CONVERTED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Frank Carter',
    clientEmail: 'frank@carterhospitality.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to hotel acquisition loan.',
  },
  REF_MITCHELL_CONVERTED: {
    id: REFERRAL_IDS.REF_MITCHELL_CONVERTED,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    clientName: 'Ruth Mitchell',
    clientEmail: 'ruth@mitchellseniorliving.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to assisted living construction loan.',
  },
  REF_PEREZ_CONVERTED: {
    id: REFERRAL_IDS.REF_PEREZ_CONVERTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    clientName: 'Maria Perez',
    clientEmail: 'maria@perezholdings.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to bridge loan. Closing this week.',
  },
  REF_ROBERTS_CONVERTED: {
    id: REFERRAL_IDS.REF_ROBERTS_CONVERTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    clientName: 'Charles Roberts',
    clientEmail: 'charles@robertsflipping.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to fix-and-flip loan. Second deal with us.',
  },
  REF_TURNER_CONVERTED: {
    id: REFERRAL_IDS.REF_TURNER_CONVERTED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    clientName: 'Susan Turner',
    clientEmail: 'susan@turnerrentals.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.CONVERTED,
    convertedToCase: true,
    notes: 'Converted to DSCR rental loan. Third property with us.',
  },

  // Lost Status
  REF_PHILLIPS_LOST: {
    id: REFERRAL_IDS.REF_PHILLIPS_LOST,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Richard Phillips',
    clientEmail: 'richard.phillips@email.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.LOST,
    convertedToCase: false,
    notes: 'Lost to competitor - better rate offered.',
  },
  REF_CAMPBELL_LOST: {
    id: REFERRAL_IDS.REF_CAMPBELL_LOST,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    clientName: 'Margaret Campbell',
    clientEmail: 'mcampbell@campbellcommercial.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.LOST,
    convertedToCase: false,
    notes: 'Deal fell through - property inspection issues.',
  },
  REF_PARKER_LOST: {
    id: REFERRAL_IDS.REF_PARKER_LOST,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    clientName: 'Joseph Parker',
    clientEmail: 'joe@parkerbuilders.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.LOST,
    convertedToCase: false,
    notes: 'Project delayed indefinitely due to permit issues.',
  },
  REF_EVANS_LOST: {
    id: REFERRAL_IDS.REF_EVANS_LOST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    clientName: 'Betty Evans',
    clientEmail: 'betty.evans@email.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.LOST,
    convertedToCase: false,
    notes: 'Decided to pay cash instead of financing.',
  },
  REF_EDWARDS_LOST: {
    id: REFERRAL_IDS.REF_EDWARDS_LOST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    clientName: 'Donald Edwards',
    clientEmail: 'don.edwards@email.com',
    referralType: REFERRAL_TYPE.INVESTOR,
    status: REFERRAL_STATUS.LOST,
    convertedToCase: false,
    notes: 'Credit score dropped below minimum threshold.',
  },
  REF_COLLINS_LOST: {
    id: REFERRAL_IDS.REF_COLLINS_LOST,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    clientName: 'Steven Collins',
    clientEmail: 'steven.collins@email.com',
    referralType: REFERRAL_TYPE.BUYER,
    status: REFERRAL_STATUS.LOST,
    convertedToCase: false,
    notes: 'Property sold to another buyer before financing approved.',
  },
};

// =============================================================================
// COMMISSION DATA
// =============================================================================

export interface CommissionSeedData {
  id: string;
  businessId: string;
  partnerId: string;
  caseId?: string;
  referralId?: string;
  amount: number; // Decimal(10, 2)
  rate: number; // Decimal(5, 2)
  status: string;
  paidAt?: Date;
  description?: string;
  metadata?: Record<string, unknown>;
}

export const COMMISSIONS: Record<keyof typeof COMMISSION_IDS, CommissionSeedData> = {
  COMM_THOMPSON_DEAL: {
    id: COMMISSION_IDS.COMM_THOMPSON_DEAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 7275, // $485,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-12-31'),
    description: 'Commission for Thompson bridge loan - funded 12/15/2025',
    metadata: {
      loanAmount: 485000,
      paymentMethod: 'ACH',
    },
  },
  COMM_FERNANDEZ_DEAL: {
    id: COMMISSION_IDS.COMM_FERNANDEZ_DEAL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 1800, // $720,000 * 0.25%
    rate: 0.25,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Referral commission for Fernandez purchase - closed 12/20/2025',
    metadata: {
      loanAmount: 720000,
      referralBonus: true,
    },
  },
  COMM_WILLIAMS_REF: {
    id: COMMISSION_IDS.COMM_WILLIAMS_REF,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    referralId: REFERRAL_IDS.REF_WILLIAMS_QUALIFIED,
    amount: 500, // Flat referral fee
    rate: 0.0, // Not percentage-based
    status: COMMISSION_STATUS.PENDING,
    description: 'Referral fee for Williams qualified lead',
  },
  COMM_PENDING_RIVERA: {
    id: COMMISSION_IDS.COMM_PENDING_RIVERA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 5625, // $450,000 * 1.25%
    rate: 1.25,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Rivera refinance - pending funding',
    metadata: {
      loanAmount: 450000,
      estimatedFunding: '2026-04-15',
    },
  },

  // === EXPANDED COMMISSIONS ===
  // Paid Commissions
  COMM_COLLINS_PAID: {
    id: COMMISSION_IDS.COMM_COLLINS_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 5925, // $395,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-20'),
    description: 'Commission for Collins bridge loan - funded 01/15/2026',
    metadata: { loanAmount: 395000, paymentMethod: 'ACH' },
  },
  COMM_STEWART_PAID: {
    id: COMMISSION_IDS.COMM_STEWART_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 5562, // $445,000 * 1.25%
    rate: 1.25,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-18'),
    description: 'Commission for Stewart bridge loan - funded 01/12/2026',
    metadata: { loanAmount: 445000, paymentMethod: 'ACH' },
  },
  COMM_SANCHEZ_PAID: {
    id: COMMISSION_IDS.COMM_SANCHEZ_PAID,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 10530, // $780,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-15'),
    description: 'Commission for Sanchez purchase - funded 01/10/2026',
    metadata: { loanAmount: 780000, paymentMethod: 'Wire' },
  },
  COMM_MORRIS_PAID: {
    id: COMMISSION_IDS.COMM_MORRIS_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 2600, // $520,000 * 0.5%
    rate: 0.5,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-14'),
    description: 'Commission for Morris bridge loan - referral partner',
    metadata: { loanAmount: 520000, paymentMethod: 'ACH' },
  },
  COMM_ROGERS_PAID: {
    id: COMMISSION_IDS.COMM_ROGERS_PAID,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 10125, // $1,350,000 * 0.75%
    rate: 0.75,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-10'),
    description: 'Commission for Rogers commercial - funded 01/05/2026',
    metadata: { loanAmount: 1350000, paymentMethod: 'Wire' },
  },
  COMM_REED_PAID: {
    id: COMMISSION_IDS.COMM_REED_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 1712, // $685,000 * 0.25%
    rate: 0.25,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-08'),
    description: 'Commission for Reed refinance - referral fee',
    metadata: { loanAmount: 685000, paymentMethod: 'ACH' },
  },
  COMM_COOK_PAID: {
    id: COMMISSION_IDS.COMM_COOK_PAID,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 28350, // $2,100,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-05'),
    description: 'Commission for Cook construction - funded 12/28/2025',
    metadata: { loanAmount: 2100000, paymentMethod: 'Wire' },
  },
  COMM_MORGAN_PAID: {
    id: COMMISSION_IDS.COMM_MORGAN_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 6975, // $465,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-12-31'),
    description: 'Commission for Morgan bridge - funded 12/22/2025',
    metadata: { loanAmount: 465000, paymentMethod: 'ACH' },
  },
  COMM_BELL_PAID: {
    id: COMMISSION_IDS.COMM_BELL_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 4812, // $385,000 * 1.25%
    rate: 1.25,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-12-15'),
    description: 'Commission for Bell flip - closed and paid off',
    metadata: { loanAmount: 385000, paymentMethod: 'ACH' },
  },
  COMM_MURPHY_PAID: {
    id: COMMISSION_IDS.COMM_MURPHY_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 2600, // $520,000 * 0.5%
    rate: 0.5,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-12-05'),
    description: 'Commission for Murphy bridge - referral partner',
    metadata: { loanAmount: 520000, paymentMethod: 'ACH' },
  },
  COMM_BAILEY_PAID: {
    id: COMMISSION_IDS.COMM_BAILEY_PAID,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 12082, // $895,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-10-25'),
    description: 'Commission for Bailey purchase - refinanced out',
    metadata: { loanAmount: 895000, paymentMethod: 'Wire' },
  },
  COMM_RIVERA_CLOSED_PAID: {
    id: COMMISSION_IDS.COMM_RIVERA_CLOSED_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 10125, // $675,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-10-01'),
    description: 'Commission for Rivera bridge - paid off',
    metadata: { loanAmount: 675000, paymentMethod: 'ACH' },
  },
  COMM_COOPER_PAID: {
    id: COMMISSION_IDS.COMM_COOPER_PAID,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 13875, // $1,850,000 * 0.75%
    rate: 0.75,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-09-10'),
    description: 'Commission for Cooper commercial - sold',
    metadata: { loanAmount: 1850000, paymentMethod: 'Wire' },
  },
  COMM_RICHARDSON_PAID: {
    id: COMMISSION_IDS.COMM_RICHARDSON_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 1062, // $425,000 * 0.25%
    rate: 0.25,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-07-25'),
    description: 'Commission for Richardson bridge - referral fee',
    metadata: { loanAmount: 425000, paymentMethod: 'ACH' },
  },
  COMM_COX_PAID: {
    id: COMMISSION_IDS.COMM_COX_PAID,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 16875, // $1,250,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-07-01'),
    description: 'Commission for Cox construction - project complete',
    metadata: { loanAmount: 1250000, paymentMethod: 'Wire' },
  },
  COMM_HOWARD_PAID: {
    id: COMMISSION_IDS.COMM_HOWARD_PAID,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 3687, // $295,000 * 1.25%
    rate: 1.25,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2025-10-05'),
    description: 'Commission for Howard flip - paid off',
    metadata: { loanAmount: 295000, paymentMethod: 'ACH' },
  },

  // Approved Commissions
  COMM_PEREZ_APPROVED: {
    id: COMMISSION_IDS.COMM_PEREZ_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 7275, // $485,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Perez bridge - closing this week',
    metadata: { loanAmount: 485000, estimatedPayout: '2026-02-15' },
  },
  COMM_ROBERTS_APPROVED: {
    id: COMMISSION_IDS.COMM_ROBERTS_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 4562, // $365,000 * 1.25%
    rate: 1.25,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Roberts flip - final docs',
    metadata: { loanAmount: 365000, estimatedPayout: '2026-02-20' },
  },
  COMM_TURNER_APPROVED: {
    id: COMMISSION_IDS.COMM_TURNER_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 2900, // $580,000 * 0.5%
    rate: 0.5,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Turner DSCR - referral partner',
    metadata: { loanAmount: 580000, estimatedPayout: '2026-02-22' },
  },
  COMM_PHILLIPS_APPROVED: {
    id: COMMISSION_IDS.COMM_PHILLIPS_APPROVED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 12420, // $920,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Phillips refi - title cleared',
    metadata: { loanAmount: 920000, estimatedPayout: '2026-02-25' },
  },
  COMM_CAMPBELL_APPROVED: {
    id: COMMISSION_IDS.COMM_CAMPBELL_APPROVED,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 17625, // $2,350,000 * 0.75%
    rate: 0.75,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Campbell commercial - wire instructions',
    metadata: { loanAmount: 2350000, estimatedPayout: '2026-02-28' },
  },
  COMM_PARKER_APPROVED: {
    id: COMMISSION_IDS.COMM_PARKER_APPROVED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 22275, // $1,650,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Parker construction - contract signing',
    metadata: { loanAmount: 1650000, estimatedPayout: '2026-03-05' },
  },
  COMM_EVANS_APPROVED: {
    id: COMMISSION_IDS.COMM_EVANS_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 2875, // $1,150,000 * 0.25%
    rate: 0.25,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Evans multifamily - referral fee',
    metadata: { loanAmount: 1150000, estimatedPayout: '2026-03-08' },
  },
  COMM_EDWARDS_APPROVED: {
    id: COMMISSION_IDS.COMM_EDWARDS_APPROVED,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 10875, // $725,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Commission for Edwards bridge - closing docs',
    metadata: { loanAmount: 725000, estimatedPayout: '2026-03-10' },
  },

  // Pending Commissions
  COMM_WALKER_PENDING: {
    id: COMMISSION_IDS.COMM_WALKER_PENDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 5100, // $340,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Walker bridge - in underwriting',
    metadata: { loanAmount: 340000, estimatedFunding: '2026-02-15' },
  },
  COMM_CHEN_PENDING: {
    id: COMMISSION_IDS.COMM_CHEN_PENDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 4687, // $937,500 * 0.5%
    rate: 0.5,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Chen purchase - processing',
    metadata: { loanAmount: 937500, estimatedFunding: '2026-03-01' },
  },
  COMM_JOHNSON_PENDING: {
    id: COMMISSION_IDS.COMM_JOHNSON_PENDING,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 32400, // $2,400,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Johnson construction - approved',
    metadata: { loanAmount: 2400000, estimatedFunding: '2026-01-30' },
  },
  COMM_NGUYEN_PENDING: {
    id: COMMISSION_IDS.COMM_NGUYEN_PENDING,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 9000, // $1,200,000 * 0.75%
    rate: 0.75,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Nguyen commercial - pre-approval',
    metadata: { loanAmount: 1200000, estimatedFunding: '2026-05-01' },
  },
  COMM_TAYLOR_PENDING: {
    id: COMMISSION_IDS.COMM_TAYLOR_PENDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 8250, // $550,000 * 1.5%
    rate: 1.5,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Taylor bridge - processing',
    metadata: { loanAmount: 550000, estimatedFunding: '2026-02-28' },
  },
  COMM_HARRIS_PENDING: {
    id: COMMISSION_IDS.COMM_HARRIS_PENDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 4750, // $380,000 * 1.25%
    rate: 1.25,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Harris renovation - appraisal pending',
    metadata: { loanAmount: 380000, estimatedFunding: '2026-02-20' },
  },
  COMM_CLARK_PENDING: {
    id: COMMISSION_IDS.COMM_CLARK_PENDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 4875, // $1,950,000 * 0.25%
    rate: 0.25,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Clark portfolio - referral',
    metadata: { loanAmount: 1950000, estimatedFunding: '2026-03-10' },
  },
  COMM_LEWIS_PENDING: {
    id: COMMISSION_IDS.COMM_LEWIS_PENDING,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 37800, // $2,800,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Lewis retail - processing',
    metadata: { loanAmount: 2800000, estimatedFunding: '2026-04-01' },
  },
  COMM_ROBINSON_PENDING: {
    id: COMMISSION_IDS.COMM_ROBINSON_PENDING,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 26250, // $3,500,000 * 0.75%
    rate: 0.75,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Robinson warehouse - environmental',
    metadata: { loanAmount: 3500000, estimatedFunding: '2026-05-01' },
  },
  COMM_HALL_PENDING: {
    id: COMMISSION_IDS.COMM_HALL_PENDING,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 56700, // $4,200,000 * 1.35%
    rate: 1.35,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Hall mixed-use - plans review',
    metadata: { loanAmount: 4200000, estimatedFunding: '2026-06-01' },
  },
  COMM_YOUNG_PENDING: {
    id: COMMISSION_IDS.COMM_YOUNG_PENDING,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 3100, // $620,000 * 0.5%
    rate: 0.5,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for Young fourplex - referral',
    metadata: { loanAmount: 620000, estimatedFunding: '2026-03-15' },
  },
  COMM_KING_PENDING: {
    id: COMMISSION_IDS.COMM_KING_PENDING,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    amount: 6675, // $890,000 * 0.75%
    rate: 0.75,
    status: COMMISSION_STATUS.PENDING,
    description: 'Commission for King condo - HOA review',
    metadata: { loanAmount: 890000, estimatedFunding: '2026-03-01' },
  },

  // Referral Commissions (flat fees)
  COMM_REF_BAKER: {
    id: COMMISSION_IDS.COMM_REF_BAKER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    referralId: REFERRAL_IDS.REF_BAKER_CONVERTED,
    amount: 1000,
    rate: 0,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Referral bonus for Baker construction deal',
  },
  COMM_REF_GONZALEZ: {
    id: COMMISSION_IDS.COMM_REF_GONZALEZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    referralId: REFERRAL_IDS.REF_GONZALEZ_CONVERTED,
    amount: 500,
    rate: 0,
    status: COMMISSION_STATUS.PAID,
    paidAt: new Date('2026-01-20'),
    description: 'Referral bonus for Gonzalez bridge deal',
  },
  COMM_REF_NELSON: {
    id: COMMISSION_IDS.COMM_REF_NELSON,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    referralId: REFERRAL_IDS.REF_NELSON_CONVERTED,
    amount: 1500,
    rate: 0,
    status: COMMISSION_STATUS.PENDING,
    description: 'Referral bonus for Nelson storage deal',
  },
  COMM_REF_CARTER: {
    id: COMMISSION_IDS.COMM_REF_CARTER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    referralId: REFERRAL_IDS.REF_CARTER_CONVERTED,
    amount: 2500,
    rate: 0,
    status: COMMISSION_STATUS.PENDING,
    description: 'Referral bonus for Carter hotel deal',
  },
  COMM_REF_MITCHELL: {
    id: COMMISSION_IDS.COMM_REF_MITCHELL,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    referralId: REFERRAL_IDS.REF_MITCHELL_CONVERTED,
    amount: 3000,
    rate: 0,
    status: COMMISSION_STATUS.PENDING,
    description: 'Referral bonus for Mitchell senior living deal',
  },
  COMM_REF_PEREZ: {
    id: COMMISSION_IDS.COMM_REF_PEREZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    referralId: REFERRAL_IDS.REF_PEREZ_CONVERTED,
    amount: 500,
    rate: 0,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Referral bonus for Perez bridge deal',
  },
  COMM_REF_ROBERTS: {
    id: COMMISSION_IDS.COMM_REF_ROBERTS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    referralId: REFERRAL_IDS.REF_ROBERTS_CONVERTED,
    amount: 750,
    rate: 0,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Referral bonus for Roberts flip - repeat client',
  },
  COMM_REF_TURNER: {
    id: COMMISSION_IDS.COMM_REF_TURNER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    referralId: REFERRAL_IDS.REF_TURNER_CONVERTED,
    amount: 600,
    rate: 0,
    status: COMMISSION_STATUS.APPROVED,
    description: 'Referral bonus for Turner DSCR - loyal client',
  },

  // Cancelled Commissions
  COMM_CANCELLED_PHILLIPS: {
    id: COMMISSION_IDS.COMM_CANCELLED_PHILLIPS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: undefined,
    amount: 8500,
    rate: 1.35,
    status: COMMISSION_STATUS.CANCELLED,
    description: 'Cancelled - Deal lost to competitor',
    metadata: { originalLoanAmount: 630000, reason: 'Competitor offer' },
  },
  COMM_CANCELLED_CAMPBELL: {
    id: COMMISSION_IDS.COMM_CANCELLED_CAMPBELL,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    partnerId: CRM_PARTNER_IDS.PACIFIC_WHOLESALE_CRM,
    caseId: undefined,
    amount: 12000,
    rate: 0.75,
    status: COMMISSION_STATUS.CANCELLED,
    description: 'Cancelled - Property inspection failed',
    metadata: { originalLoanAmount: 1600000, reason: 'Failed inspection' },
  },
  COMM_CANCELLED_PARKER: {
    id: COMMISSION_IDS.COMM_CANCELLED_PARKER,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    caseId: undefined,
    amount: 18000,
    rate: 1.35,
    status: COMMISSION_STATUS.CANCELLED,
    description: 'Cancelled - Permit issues halted project',
    metadata: { originalLoanAmount: 1333000, reason: 'Permit denial' },
  },
  COMM_CANCELLED_EVANS: {
    id: COMMISSION_IDS.COMM_CANCELLED_EVANS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.RE_INVESTOR_ASSOC_CRM,
    caseId: undefined,
    amount: 1500,
    rate: 0.25,
    status: COMMISSION_STATUS.CANCELLED,
    description: 'Cancelled - Borrower paid cash',
    metadata: { originalLoanAmount: 600000, reason: 'Cash purchase' },
  },
  COMM_CANCELLED_EDWARDS: {
    id: COMMISSION_IDS.COMM_CANCELLED_EDWARDS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    caseId: undefined,
    amount: 5400,
    rate: 1.5,
    status: COMMISSION_STATUS.CANCELLED,
    description: 'Cancelled - Credit score dropped',
    metadata: { originalLoanAmount: 360000, reason: 'Credit deterioration' },
  },
};

// =============================================================================
// COMMISSION SETTINGS DATA
// =============================================================================

export interface CommissionSettingsSeedData {
  id: string;
  businessId: string;
  defaultRate: number; // Decimal(5, 2)
  payoutSchedule: 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';
  minimumPayout: number; // Decimal(10, 2)
}

export const COMMISSION_SETTINGS: Record<keyof typeof COMMISSION_SETTINGS_IDS, CommissionSettingsSeedData> = {
  OPSMATRIX_SETTINGS: {
    id: COMMISSION_SETTINGS_IDS.OPSMATRIX_SETTINGS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    defaultRate: 1.0,
    payoutSchedule: 'MONTHLY',
    minimumPayout: 100,
  },
  SHARPE_SETTINGS: {
    id: COMMISSION_SETTINGS_IDS.SHARPE_SETTINGS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    defaultRate: 1.25,
    payoutSchedule: 'BIWEEKLY',
    minimumPayout: 250,
  },
  PACIFIC_SETTINGS: {
    id: COMMISSION_SETTINGS_IDS.PACIFIC_SETTINGS,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    defaultRate: 0.75,
    payoutSchedule: 'MONTHLY',
    minimumPayout: 500,
  },
  GULFCOAST_SETTINGS: {
    id: COMMISSION_SETTINGS_IDS.GULFCOAST_SETTINGS,
    businessId: BUSINESS_IDS.GULF_COAST_FO,
    defaultRate: 0.5,
    payoutSchedule: 'MONTHLY',
    minimumPayout: 1000,
  },
};

// =============================================================================
// COMMISSION TIER DATA
// =============================================================================

export interface CommissionTierSeedData {
  id: string;
  businessId: string;
  name: string;
  minVolume: number; // Decimal(15, 2)
  maxVolume?: number; // Decimal(15, 2) - null = unlimited
  rate: number; // Decimal(5, 2)
  sortOrder: number;
}

export const COMMISSION_TIERS: Record<keyof typeof COMMISSION_TIER_IDS, CommissionTierSeedData> = {
  // OpsMatrix Capital tiers
  OPSMATRIX_BRONZE: {
    id: COMMISSION_TIER_IDS.OPSMATRIX_BRONZE,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Bronze',
    minVolume: 0,
    maxVolume: 1000000,
    rate: 1.0,
    sortOrder: 1,
  },
  OPSMATRIX_SILVER: {
    id: COMMISSION_TIER_IDS.OPSMATRIX_SILVER,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Silver',
    minVolume: 1000000,
    maxVolume: 5000000,
    rate: 1.25,
    sortOrder: 2,
  },
  OPSMATRIX_GOLD: {
    id: COMMISSION_TIER_IDS.OPSMATRIX_GOLD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Gold',
    minVolume: 5000000,
    maxVolume: 15000000,
    rate: 1.5,
    sortOrder: 3,
  },
  OPSMATRIX_PLATINUM: {
    id: COMMISSION_TIER_IDS.OPSMATRIX_PLATINUM,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Platinum',
    minVolume: 15000000,
    maxVolume: undefined, // Unlimited
    rate: 1.75,
    sortOrder: 4,
  },

  // 1Sharpe Capital tiers
  SHARPE_STANDARD: {
    id: COMMISSION_TIER_IDS.SHARPE_STANDARD,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Standard',
    minVolume: 0,
    maxVolume: 2000000,
    rate: 1.0,
    sortOrder: 1,
  },
  SHARPE_PREFERRED: {
    id: COMMISSION_TIER_IDS.SHARPE_PREFERRED,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Preferred',
    minVolume: 2000000,
    maxVolume: 10000000,
    rate: 1.35,
    sortOrder: 2,
  },
  SHARPE_ELITE: {
    id: COMMISSION_TIER_IDS.SHARPE_ELITE,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Elite',
    minVolume: 10000000,
    maxVolume: undefined,
    rate: 1.6,
    sortOrder: 3,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all leads as an array for seeding
 */
export function getAllLeads(): LeadSeedData[] {
  return Object.values(LEADS);
}

/**
 * Get leads by business
 */
export function getLeadsByBusiness(businessId: string): LeadSeedData[] {
  return Object.values(LEADS).filter((l) => l.businessId === businessId);
}

/**
 * Get leads by status
 */
export function getLeadsByStatus(status: string): LeadSeedData[] {
  return Object.values(LEADS).filter((l) => l.status === status);
}

/**
 * Get all CRM partners as an array for seeding
 */
export function getAllCrmPartners(): CrmPartnerSeedData[] {
  return Object.values(CRM_PARTNERS);
}

/**
 * Get CRM partners by business
 */
export function getCrmPartnersByBusiness(businessId: string): CrmPartnerSeedData[] {
  return Object.values(CRM_PARTNERS).filter((p) => p.businessId === businessId);
}

/**
 * Get all referrals as an array for seeding
 */
export function getAllReferrals(): ReferralSeedData[] {
  return Object.values(REFERRALS);
}

/**
 * Get referrals by business
 */
export function getReferralsByBusiness(businessId: string): ReferralSeedData[] {
  return Object.values(REFERRALS).filter((r) => r.businessId === businessId);
}

/**
 * Get all commissions as an array for seeding
 */
export function getAllCommissions(): CommissionSeedData[] {
  return Object.values(COMMISSIONS);
}

/**
 * Get commissions by business
 */
export function getCommissionsByBusiness(businessId: string): CommissionSeedData[] {
  return Object.values(COMMISSIONS).filter((c) => c.businessId === businessId);
}

/**
 * Get commissions by status
 */
export function getCommissionsByStatus(status: string): CommissionSeedData[] {
  return Object.values(COMMISSIONS).filter((c) => c.status === status);
}

/**
 * Get all commission settings as an array for seeding
 */
export function getAllCommissionSettings(): CommissionSettingsSeedData[] {
  return Object.values(COMMISSION_SETTINGS);
}

/**
 * Get all commission tiers as an array for seeding
 */
export function getAllCommissionTiers(): CommissionTierSeedData[] {
  return Object.values(COMMISSION_TIERS);
}

/**
 * Get commission tiers by business
 */
export function getCommissionTiersByBusiness(businessId: string): CommissionTierSeedData[] {
  return Object.values(COMMISSION_TIERS).filter((t) => t.businessId === businessId);
}

// =============================================================================
// COMMISSION RATE HISTORY IDs (Issue #267)
// =============================================================================

export const COMMISSION_RATE_HISTORY_IDS = {
  // Premier Mortgage rate changes
  PREMIER_RATE_UPGRADE_1: seedId('rate-history-premier-1', 1),
  PREMIER_RATE_UPGRADE_2: seedId('rate-history-premier-2', 2),

  // Austin Mortgage rate changes
  AUSTIN_RATE_UPGRADE: seedId('rate-history-austin-1', 1),

  // Florida First rate change
  FLORIDA_RATE_ADJUSTMENT: seedId('rate-history-florida-1', 1),

  // Capital Funding rate change (referral partner)
  CAPITAL_RATE_INCREASE: seedId('rate-history-capital-1', 1),
} as const;

export type CommissionRateHistoryId = (typeof COMMISSION_RATE_HISTORY_IDS)[keyof typeof COMMISSION_RATE_HISTORY_IDS];

// =============================================================================
// COMMISSION RATE HISTORY DATA TYPE
// =============================================================================

export interface CommissionRateHistorySeedData {
  id: string;
  partnerId: string; // CRM Partner ID
  oldRate: number; // Decimal(5, 2) - previous rate
  newRate: number; // Decimal(5, 2) - new rate
  reason: string; // Reason for the rate change
  changedBy: string; // User ID who made the change
  changedAt: string; // ISO8601 timestamp
}

// =============================================================================
// COMMISSION RATE HISTORY DATA
// =============================================================================

export const COMMISSION_RATE_HISTORIES: Record<
  keyof typeof COMMISSION_RATE_HISTORY_IDS,
  CommissionRateHistorySeedData
> = {
  // Premier Mortgage - upgraded from 1.25% to 1.5% (Silver to Gold tier)
  PREMIER_RATE_UPGRADE_1: {
    id: COMMISSION_RATE_HISTORY_IDS.PREMIER_RATE_UPGRADE_1,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    oldRate: 1.25,
    newRate: 1.5,
    reason: 'Volume threshold achieved - upgraded to Gold tier ($5M monthly)',
    changedBy: 'system',
    changedAt: '2025-06-15T00:00:00Z',
  },
  // Premier Mortgage - earlier upgrade from 1.0% to 1.25% (Bronze to Silver tier)
  PREMIER_RATE_UPGRADE_2: {
    id: COMMISSION_RATE_HISTORY_IDS.PREMIER_RATE_UPGRADE_2,
    partnerId: CRM_PARTNER_IDS.PREMIER_MORTGAGE_CRM,
    oldRate: 1.0,
    newRate: 1.25,
    reason: 'Volume threshold achieved - upgraded to Silver tier ($1M monthly)',
    changedBy: 'system',
    changedAt: '2025-03-01T00:00:00Z',
  },

  // Austin Mortgage - upgraded based on performance
  AUSTIN_RATE_UPGRADE: {
    id: COMMISSION_RATE_HISTORY_IDS.AUSTIN_RATE_UPGRADE,
    partnerId: CRM_PARTNER_IDS.AUSTIN_MORTGAGE_CRM,
    oldRate: 1.0,
    newRate: 1.25,
    reason: 'Performance bonus - 95% on-time document submission rate',
    changedBy: 'system',
    changedAt: '2025-09-01T00:00:00Z',
  },

  // Florida First - rate adjustment for preferred partner status
  FLORIDA_RATE_ADJUSTMENT: {
    id: COMMISSION_RATE_HISTORY_IDS.FLORIDA_RATE_ADJUSTMENT,
    partnerId: CRM_PARTNER_IDS.FLORIDA_FIRST_CRM,
    oldRate: 1.25,
    newRate: 1.35,
    reason: 'Preferred partner agreement negotiated - Q3 2025',
    changedBy: 'system',
    changedAt: '2025-08-10T00:00:00Z',
  },

  // Capital Funding - referral partner rate increase
  CAPITAL_RATE_INCREASE: {
    id: COMMISSION_RATE_HISTORY_IDS.CAPITAL_RATE_INCREASE,
    partnerId: CRM_PARTNER_IDS.CAPITAL_FUNDING_CRM,
    oldRate: 0.25,
    newRate: 0.5,
    reason: 'Referral quality bonus - 80%+ conversion rate achieved',
    changedBy: 'system',
    changedAt: '2025-07-01T00:00:00Z',
  },
};

// =============================================================================
// COMMISSION RATE HISTORY HELPER FUNCTIONS
// =============================================================================

/**
 * Get all commission rate history entries
 */
export function getAllCommissionRateHistories(): CommissionRateHistorySeedData[] {
  return Object.values(COMMISSION_RATE_HISTORIES);
}

/**
 * Get rate history by partner
 */
export function getCommissionRateHistoriesByPartner(partnerId: string): CommissionRateHistorySeedData[] {
  return Object.values(COMMISSION_RATE_HISTORIES).filter((h) => h.partnerId === partnerId);
}

/**
 * Get rate history entries by changedBy user
 */
export function getCommissionRateHistoriesByUser(userId: string): CommissionRateHistorySeedData[] {
  return Object.values(COMMISSION_RATE_HISTORIES).filter((h) => h.changedBy === userId);
}
